import { useEffect, useRef, useState } from "react";
import { Label, Modal } from "flowbite-react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useFormik } from "formik";
import { getMessagesForR, getMessagesForT, resSendMessage, travSendMessage } from "../../services/callAPI/toChat";
import UnvalidToast from "../toasts/UnvalidToast";
// import { useSocketByRole } from "../../services/hooks/useSocketByRole";
import { object, string } from "yup";
import { isSanitizeMessageChatValue } from "../../services/hooks/sanitizeValue/isSanitizeValue";
import { useSocket } from "../../App";

export interface Message {
  sender: string;
  content: string;
  type: "received" | "sent";
}

export interface ChatComponentProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>; // Permet d'utiliser une fonction de mise à jour
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsShowUnvalidToast: React.Dispatch<React.SetStateAction<boolean>>;
  res_troc_id: number;
  trav_troc_id: number;
  userRole?: string | null;
  isLoadingMessages: boolean;
}

/* main component */
function DiscussModal(props: { res_troc_id: number, trav_troc_id: number }) {
  //destucturing
  const { res_troc_id, trav_troc_id } = props;

  const [message, _setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);

  const [openModal, setOpenModal] = useState(false);
  const [isShowUnvalidToast, setIsShowUnvalidToast] = useState<boolean>(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  //get user role via redux
  const userRole = useSelector((state: RootState) => state.user.role);

  //useEffect to get conversations all
  useEffect(() => {
    if (!openModal) return;

    //pick-up conversations
    const handleConversations = async () => {
      //enable loading
      setIsLoadingMessages(true);

      try {
        //call api
        const response: number | { discussions: Message[] } =
          userRole === "TRAVELER"
            ? await getMessagesForT(res_troc_id)
            : await getMessagesForR(trav_troc_id);

        if (typeof response === "number") {
          null
        }
        else if (response && typeof response === "object" && "discussions" in response) {
          setMessages(response.discussions);
        }
        else {
          throw new Error(`type de reponse inconnue ${typeof response}`);
        }
      }
      catch (error) {
        setOpenModal(false);
      }
      finally {
        setIsLoadingMessages(false);
      }
    }
    handleConversations()

  }, [openModal, message])

  return (
    <>
      <button className="w-1/2 max-w-32 p-0 bg-orangeCustom rounded-md py-1 px-4 text-white" onClick={() => setOpenModal(true)}>Messagerie</button>

      <Modal className="p-6 bg-transparent backdrop-blur-sm" show={openModal} size="md" popup onClose={() => setOpenModal(false)}>
        <Modal.Body>
          <ChatComponent
            userRole={userRole}
            messages={messages}
            setMessages={setMessages}
            setOpenModal={setOpenModal}
            setIsShowUnvalidToast={setIsShowUnvalidToast}
            res_troc_id={res_troc_id}
            trav_troc_id={trav_troc_id}
            isLoadingMessages={isLoadingMessages}
          />
        </Modal.Body>
      </Modal>

      {isShowUnvalidToast && <UnvalidToast />}
    </>
  )
}
export default DiscussModal;

/* //////////////////////////////child component //////////////////////*/
function ChatComponent(props: ChatComponentProps) {
  //use context selon le role
  // const socket = useSocketByRole();
  const socket = useSocket();

  //destructuring
  const { messages, setMessages, setOpenModal, setIsShowUnvalidToast, res_troc_id, trav_troc_id, userRole, isLoadingMessages } = props;

  // Référence pour le dernier message
  const lastMessageRef = useRef<HTMLDivElement>(null);

  //set validation schema with Yup
  const messageSchema = object({
    content: string()
      .trim()
      .min(2, "Le message doit comporter au moins 2 caractères").max(160, "Le message ne peut pas dépasser 160 caractères")
      .test(
        "is-message-valid",
        "le message contient des caractères interdits",
        (value) => isSanitizeMessageChatValue(value)
      )
      .required(`Vous avez oublié votre message`),
  })

  // set formik
  const { handleSubmit, handleChange, errors, touched, values, resetForm, isSubmitting } = useFormik({
    initialValues: {
      content: "",
    },
    validationSchema: messageSchema,
    onSubmit: async values => {
      // Ajoute le message de l'utilisateur immédiatement dans l'état des messages
      setMessages((prevMessages: Message[]) => [
        ...prevMessages,
        { sender: "Moi", content: values.content, type: "sent" }
      ]);

      try {
        let response;

        if (userRole === "TRAVELER") {
          if (!socket) {
            console.error("[Socket] Socket non disponible. Impossible d'envoyer le message.");
            return;
          }
          //call Api
          response = await travSendMessage({ content: values.content, res_troc_id, trav_troc_id, socket });
        }
        else if (userRole === "RESIDENT") {
          if (!socket) {
            console.error("[Socket] Socket non disponible. Impossible d'envoyer le message.");
            return;
          }
          //call Api
          response = await resSendMessage({ content: values.content, res_troc_id, trav_troc_id, socket });
        }

        if (response !== "envoyé") { //failure 
          throw new Error(`la réponse n'est pas égal a envoyé: ${response}`);
        }

        //reset form
        resetForm();
      }
      catch (error) {
        //delete last msg sent of traveler 
        setMessages((prevMessages: Message[]) => {
          const updatedMessages = [...prevMessages];  // create messages array copy

          updatedMessages.pop(); //delete last msg

          return updatedMessages;
        });

        //reset form
        resetForm();

        //close modal
        setOpenModal(false);

        //show UnvalidToast
        setIsShowUnvalidToast(true);

        // missing toast next 3s
        setTimeout(() => {
          setIsShowUnvalidToast(false);
        }, 3000);
      }
    }
  })

  //listener when msg reçu 
  useEffect(() => {
    if (!socket) return;
    const eventName = `receiveMessage-rTrocId-${res_troc_id}-tTrocId-${trav_troc_id}`;

    const handleMessage = (msg: Message) => {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: msg.sender, content: msg.content, type: 'received' }
      ]);
    };

    //listener event receiveMessage
    socket.on(eventName, handleMessage);

    return () => {
      socket?.off(eventName, handleMessage);
    };
  }, [socket, res_troc_id, trav_troc_id])

  // Fonction pour défiler jusqu'au dernier message
  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView();
    }
  }, [messages]); // Se déclenche chaque fois que `messages` change

  return (
    <div className="flex flex-col gap-4 w-full text-sm sm:text-base px-2 sm:px-0">
      <h3 className="text-center text-base sm:text-xl font-semibold text-[#335C67]">💬 Messagerie</h3>

      {isLoadingMessages ? (
        <div className="text-center text-gray-400 italic">Chargement des messages...</div>
      ) : (
        <div className="messages-container overflow-y-auto max-h-80 min-h-40 p-2 sm:p-4 rounded-xl border border-gray-200 bg-[#f8f9fa] shadow-inner">
          {messages.length === 0 ? (
            <p className="text-gray-400 text-center italic">Aucun message pour le moment</p>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                ref={index === messages.length - 1 ? lastMessageRef : null}
                className={`flex mb-2 ${msg.type === 'sent' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`px-3 py-2 rounded-xl max-w-[90%] sm:max-w-[75%] text-[13px] sm:text-sm leading-relaxed whitespace-pre-wrap ${msg.type === 'sent' ? 'bg-greenCustom text-white' : 'bg-gray-100 text-gray-800'}`}>
                  <strong className="block mb-1 text-xs opacity-70">{msg.sender}</strong>
                  {msg.content}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:gap-4">
        <div>
          <Label htmlFor="content" value="Votre message" className="text-xs sm:text-sm text-gray-700" />
          <textarea
            id="content"
            name="content"
            maxLength={160}
            value={values.content}
            onChange={handleChange}
            placeholder="Écrire un message..."
            className="w-full mt-1 p-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-orangeCustom text-sm sm:text-base min-h-[80px]"
          />
          <div className="flex flex-col sm:flex-row justify-between mt-1 text-xs text-gray-400">
            <p>160 caractères max</p>
            {touched.content && errors.content && <span className="text-red-500 mt-1 sm:mt-0">{errors.content}</span>}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`py-2 px-4 rounded-xl text-white font-medium w-full transition text-sm ${isSubmitting ? 'bg-green-400 cursor-not-allowed' : 'bg-greenCustom hover:bg-green-700'
              }`}
          >
            {isSubmitting ? 'Envoi...' : 'Envoyer'}
          </button>
          <button
            type="button"
            className="bg-lightRedCustom hover:bg-red-700 py-2 px-4 rounded-xl text-white font-medium w-full transition text-sm"
            onClick={() => {
              resetForm();
              setOpenModal(false);
            }}
          >
            Fermer
          </button>
        </div>
      </form>
    </div>
  );
}